/**
 * API factory for the plan-research feature.
 * Pass an httpClient whose get/post/delete methods return [error, data] tuples.
 * See httpClientAdapter.js (Core) or the legacy SPA's planResearchApi for
 * conforming implementations.
 */
export default function (httpClient) {
  return {
    searchPlans(filters) {
      return httpClient.get('/api/plan-research/form5500/', { params: filters })
    },
    getEntitlement() {
      return httpClient.get('/api/plan-research/form5500/entitlement/')
    },
    getPlanDetail(id) {
      return httpClient.get(`/api/plan-research/form5500/${encodeURIComponent(id)}/`)
    },
    getPlanHistory(planId) {
      return httpClient.get('/api/plan-research/form5500/history/', { params: { plan_id: planId } })
    },
    // Same rows as getPlanHistory, for the one plan pinned to an engagement
    // year, authorized by engagement access instead of the plan_research flag.
    // Core's Historical Analysis page uses this because getPlanHistory is
    // full-tier and 403s a non-subscriber — who is exactly who that page is for.
    // Encoded because the id comes from the URL.
    getPlanHistoryForEngagementYear(engagementYearId) {
      return httpClient.get(
        `/api/plan-research/engagement-years/${encodeURIComponent(engagementYearId)}/filing-history/`
      )
    },
    getPeerBenchmarks({ planYear, peerDimension, peerValue }) {
      return httpClient.get('/api/plan-research/form5500/peers/', {
        params: { plan_year: planYear, peer_dimension: peerDimension, peer_value: peerValue }
      })
    },
    starPlan(id, starred) {
      return httpClient.post('/api/plan-research/form5500/star/', {
        acknowledgement_id: id,
        starred
      })
    },
    getStarredPlans() {
      return httpClient.get('/api/plan-research/form5500/starred/')
    },
    getStats() {
      return httpClient.get('/api/plan-research/form5500/stats/')
    },
    getPlanYears() {
      return httpClient.get('/api/plan-research/form5500/plan-years/')
    },
    getAuditOpinions() {
      return httpClient.get('/api/plan-research/form5500/audit-opinions/')
    },
    getBusinessSectors() {
      return httpClient.get('/api/plan-research/form5500/business-sectors/')
    },
    getFilingStatuses() {
      return httpClient.get('/api/plan-research/form5500/filing-statuses/')
    },
    getBenefitCodes() {
      return httpClient.get('/api/plan-research/form5500/benefit-codes/')
    },
    getDomainFinderSweeps(id) {
      return httpClient.get(
        `/api/plan-research/form5500/${encodeURIComponent(id)}/domain-finder-sweeps/`
      )
    },
    createDomainFinderSweep(id) {
      return httpClient.post(
        `/api/plan-research/form5500/${encodeURIComponent(id)}/domain-finder-sweeps/`
      )
    },
    getEmailFinderAttempts(id) {
      return httpClient.get(
        `/api/plan-research/form5500/${encodeURIComponent(id)}/email-finder-attempts/`
      )
    },
    createEmailFinderAttempt(id, domain) {
      return httpClient.post(
        `/api/plan-research/form5500/${encodeURIComponent(id)}/email-finder-attempts/`,
        { domain }
      )
    },
    getSavedSearches() {
      return httpClient.get('/api/plan-research/saved-searches/')
    },
    createSavedSearch(name, filtersJson) {
      return httpClient.post('/api/plan-research/saved-searches/', {
        name,
        filters_json: filtersJson
      })
    },
    deleteSavedSearch(id) {
      return httpClient.delete(`/api/plan-research/saved-searches/${id}/`)
    },
    // Saved-search alerts. `savedSearch` scopes the feed to one search and
    // `unread` narrows it to what the user has not opened — the same endpoint in
    // every case, so the panel, the pills and the bell cannot drift apart.
    getAlerts({ savedSearch = null, page = 1, unread = false, pageSize = null } = {}) {
      return httpClient.get('/api/plan-research/alerts/', {
        params: {
          ...(savedSearch ? { saved_search: savedSearch } : {}),
          ...(unread ? { unread: 1 } : {}),
          ...(pageSize ? { page_size: pageSize } : {}),
          page
        }
      })
    },
    // Badge state, user-wide. An explicit POST, never a side effect of a GET:
    // clearing is irreversible server-side.
    markAlertsSeen() {
      return httpClient.post('/api/plan-research/saved-searches/mark-seen/', {})
    },
    // Item state, per alert. Separate from seen so the panel can still show
    // which plans were new after the pills have gone.
    markAlertRead(id) {
      return httpClient.post(`/api/plan-research/alerts/${id}/read/`, {})
    },
    dismissAlert(id) {
      return httpClient.post(`/api/plan-research/alerts/${id}/dismiss/`, {})
    },
    // Clear all. Scoped by ?saved_search= so it clears exactly what the panel is
    // showing — the whole scope, not the loaded page. The scope rides in the URL
    // because the two httpClients behind this adapter take only (url, body).
    dismissAllAlerts({ savedSearch = null } = {}) {
      const scope = savedSearch ? `?saved_search=${encodeURIComponent(savedSearch)}` : ''
      return httpClient.post(`/api/plan-research/alerts/dismiss-all/${scope}`, {})
    },
    searchByEin(query) {
      return httpClient.get('/api/plan-research/form5500/ein_search/', { params: { q: query } })
    },
    // Triggers a browser file download — cannot follow the passthrough pattern because
    // Blob URLs must be created and revoked client-side after the response arrives.
    async exportCsv(filters) {
      const [error, blob] = await httpClient.get('/api/plan-research/form5500/export/', {
        params: filters,
        responseType: 'blob'
      })
      if (error) return [error, null]
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'form5500-export.csv'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      return [null, true]
    }
  }
}
